import { EventType } from '../events/event-types.js'

/**
 * The tenant seeding contract (TO-375, ADR-0044): what every bounded context that
 * seeds per-tenant data on `tenant.created` reports, and what the platform
 * expects to hear from.
 */

/** A context's provisioning state for one tenant, as the Super-Admin sees it. */
export enum TenantSeedStatus {
	PENDING = 'PENDING',
	COMPLETED = 'COMPLETED',
	FAILED = 'FAILED',
}

/** The `errorCode` a seeder reports when a failing item carries no usable code of its own. */
export enum TenantSeedErrorCode {
	SEED_ITEM_FAILED = 'SEED_ITEM_FAILED',
}

/** A bounded-context name: lower-case kebab, 2–63 characters. */
export const TENANT_SEED_CONTEXT_PATTERN = /^[a-z][a-z0-9-]{1,62}$/
/** A reported error code: upper-case snake, 1–64 characters. */
export const TENANT_SEED_ERROR_CODE_PATTERN = /^[A-Z][A-Z0-9_]{0,63}$/
export const TENANT_SEEDER_VERSION_MAX = 1_000_000
export const TENANT_SEED_MESSAGE_MAX = 1000
/** Upper bound on a reported item count — the `int4` ceiling of its DB column, so an
 * out-of-range count is rejected at parse time (dropped) rather than surviving to the
 * insert and burning SQS redeliveries before the DLQ. */
export const TENANT_SEED_ITEM_COUNT_MAX = 2_147_483_647

/** One expected seeder: the context that owns it and the version it currently runs. */
export interface TenantSeederRegistration {
	context: string
	seederVersion: number
}

/**
 * The expected seeders. A context adds its entry in the same change that ships
 * its seeder, and documents it in `docs/reference/seed-catalog.md`. Each seeder
 * reports the version of its entry (a test per context keeps them equal).
 * The MVP set is TO-376's. The `tenant-seeder` plop generator adds entries at
 * the marker below (TO-442).
 */
export const TENANT_SEEDER_REGISTRY: readonly TenantSeederRegistration[] = [
	{ context: 'content', seederVersion: 2 },
	{ context: 'file-exchange', seederVersion: 1 },
	{ context: 'organization', seederVersion: 1 },
	{ context: 'product', seederVersion: 1 },
	{ context: 'webhook-payload-shapes', seederVersion: 1 },
	{ context: 'brand', seederVersion: 1 },
	{ context: 'offer', seederVersion: 1 },
	{ context: 'ai-prompts', seederVersion: 1 },
	// PLOP NOT DELETE: ADD NEW TENANT SEEDERS
]

/**
 * The wiring every context's `tenant.created` seeding worker uses (TO-376
 * REQ-008): its own queue subscribed to exactly `tenant.created`, a DLQ after 3
 * receives, a visibility timeout of 2× the worker timeout, and partial batch
 * responses. Read by the SST files and asserted by each context's tests.
 */
export const TENANT_SEED_WORKER_WIRING = {
	eventTypes: [EventType.TENANT_CREATED],
	dlqMaxReceives: 3,
	timeoutSeconds: 60,
	visibilityTimeoutSeconds: 120,
	batch: { size: 10, window: '20 seconds', partialResponses: true },
} as const

/** One row of `GET /admin/tenants/{tenantId}/provisioning`. */
export interface TenantProvisioningContextDTO {
	context: string
	status: TenantSeedStatus
	/** True when the context is in the seeder registry. */
	expected: boolean
	expectedSeederVersion: number | null
	seederVersion: number | null
	reportedAt: string | null
	itemsCreated: number | null
	itemsSkipped: number | null
	errorCode: string | null
	message: string | null
}

/** `GET /admin/tenants/{tenantId}/provisioning` (TO-375 REQ-008). */
export interface TenantProvisioningStatusDTO {
	tenantId: string
	lastRetryAt: string | null
	contexts: TenantProvisioningContextDTO[]
}

/** `POST /admin/tenants/{tenantId}/provisioning/retry` → 202 (TO-375 REQ-009). */
export interface TenantProvisioningRetryDTO {
	tenantId: string
	/** The id of the re-published `tenant.created`. */
	eventId: string
	requestedAt: string
}

/** `POST /admin/tenants/{tenantId}/provisioning/contexts/{context}/retry` → 202 (TO-655 REQ-014). */
export interface TenantProvisioningContextRetryDTO extends TenantProvisioningRetryDTO {
	/** The one context whose seeder re-runs. */
	context: string
}
